'use client';

import { Stages } from '@/layers/business-logic/medical-docs/types';
import { Progress } from '@nextui-org/react';
import { useCallback, useEffect, useRef, useState } from 'react';

interface StageProgressProps {
  stages: Stages;
  className?: string;
}

export function StageProgress({ stages, className = '' }: StageProgressProps) {
  // Progress value from 0-100
  const [progressValue, setProgressValue] = useState(0);
  const animationRef = useRef<NodeJS.Timeout | null>(null);
  const targetValueRef = useRef<number>(0);
  const virtualTargetRef = useRef<number>(0);
  const lastUpdateTimeRef = useRef<number>(Date.now());

  // Create animation between current and target progress values
  const startProgressAnimation = useCallback(() => {
    // Clear any existing interval
    clearAnimationInterval();

    // Set up new interval for smooth animation
    animationRef.current = setInterval(() => {
      setProgressValue(currentValue => {
        const now = Date.now();
        const timeElapsed = now - lastUpdateTimeRef.current;

        // If we reached actual target, use it
        if (currentValue >= targetValueRef.current) {
          // Set virtual target to the next possible stage value
          const stagesArray = Object.values(stages);
          if (stagesArray.length) {
            const stageStep = 100 / stagesArray.length;
            virtualTargetRef.current = Math.min(100, targetValueRef.current + stageStep / 2);
          }

          // Continue with virtual progress
          // Slow down over time using the elapsed time since last real update
          const slowdownFactor = Math.max(0.01, 1 / (1 + timeElapsed / 25000));
          const virtDiff = virtualTargetRef.current - currentValue;

          // Very small increment that gets smaller as we approach virtual target
          const virtIncrement = Math.max(0.02, virtDiff * 0.01 * slowdownFactor);

          // Only advance if we're not too close to virtual target
          if (virtDiff > 0.2) {
            return Math.min(virtualTargetRef.current - 0.2, currentValue + virtIncrement);
          }
          return currentValue;
        }

        // Normal animation toward the real target
        const diff = targetValueRef.current - currentValue;
        const increment = Math.max(0.5, Math.min(1.5, diff / 10));

        return Math.min(targetValueRef.current, currentValue + increment);
      });
    }, 50); // Update every 50ms for smooth animation
  }, [stages]);

  // Calculate the target progress based on stages status
  useEffect(() => {
    const stagesArray = Object.values(stages);

    if (!stagesArray.length) {
      targetValueRef.current = 0;
      setProgressValue(0);
      return;
    }

    // Count completed stages
    const completedCount = stagesArray.filter(stage => stage.status === 'COMPLETE').length;

    // Count in-progress stages (count as 0.5 completion)
    const inProgressCount = stagesArray.filter(stage => stage.status === 'IN_PROGRESS').length;

    // Calculate target value: completed stages + half of in-progress stages
    const targetValue = Math.floor(
      ((completedCount + inProgressCount * 0.5) / stagesArray.length) * 100,
    );

    // Only update the lastUpdateTime reference if stages actually changed
    if (targetValue !== targetValueRef.current) {
      lastUpdateTimeRef.current = Date.now();
      targetValueRef.current = targetValue;
    }

    // If any stage failed, cap progress at current value
    if (stagesArray.some(stage => stage.status === 'FAILED')) {
      clearAnimationInterval();
    } else {
      // Start or continue animation
      startProgressAnimation();
    }

    return () => {
      // Don't clear the animation on unmount,
      // we'll handle this in the component that uses StageProgress
    };
  }, [stages, startProgressAnimation]);

  const clearAnimationInterval = () => {
    if (animationRef.current) {
      clearInterval(animationRef.current);
      animationRef.current = null;
    }
  };

  // Get proper label based on stages status
  const getProgressLabel = () => {
    const stagesArray = Object.values(stages);
    const inProgressStage = stagesArray.find(stage => stage.status === 'IN_PROGRESS');

    if (inProgressStage) {
      return inProgressStage.message || inProgressStage.name;
    }

    const failedStage = stagesArray.find(stage => stage.status === 'FAILED');
    if (failedStage) {
      return `Error: ${failedStage.message || 'Processing failed'}`;
    }

    if (stagesArray.every(stage => stage.status === 'COMPLETE')) {
      return 'Processing complete';
    }

    return 'Processing document...';
  };

  // Calculate color based on status
  const getProgressColor = () => {
    const stagesArray = Object.values(stages);

    if (stagesArray.some(stage => stage.status === 'FAILED')) {
      return 'danger';
    }
    return 'primary';
  };

  // Cleanup animation when component unmounts
  useEffect(() => {
    return () => {
      clearAnimationInterval();
    };
  }, []);

  return (
    <div className={`w-full space-y-2 ${className}`}>
      <Progress
        size="sm"
        radius="sm"
        value={progressValue}
        color={getProgressColor()}
        showValueLabel={true}
        aria-label={getProgressLabel()}
        // label={getProgressLabel()}
        classNames={{
          base: 'w-full',
          labelWrapper: 'w-full justify-end',
          label: 'text-sm text-default-600',
          value: 'text-sm font-medium text-default-600',
        }}
      />
    </div>
  );
}
